import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Row, Col, Button, TabContent, TabPane } from 'reactstrap';
import { sumBy } from 'lodash';
import { animateScroll } from 'react-scroll';

import SummaryModal from '../Vendor/Modal';
import BZAPIService from '../../../services/BZAPIService';
import { useScheduling } from '../../../hooks/useScheduling';
import ProductPriority from '../section/ProductPriority';
import DefaultConfigTab from '../section/DefaultConfigTab';
import TabNav from '../section/TabNav';
import TabOne from '../section/TabOne';
import ExecutionStatsTab from '../section/ExecutionStatsTab';
import RiskScore from '../section/RiskScore';
import AITab from '../section/AITab';

// constants
import SETTINGS from '../../../constants/settings';

const ConfigFormMongoDB = ({
  vendor,
  vendorLabel,
  isReadOnly,
  vendorId,
  bugsDateBack,
  setBugsDateBack,
  vendorPriorityOptions,
  vendorStatusOptions,
  vendorResolutionOptions,
  snIntegrationCheck,
  services,
  vendors,
  // bugs,
  priorityStats,
  // setPriorityStats,
  // bugOptions,
  // selectedBugOption,
  // setSelectedBugOption,
  snCompanySysIdOptions,
  snCompanySysIdErrors,
  vendorStatuses,
  setVendorStatuses,
  vendorResolutions,
  setVendorResolutions,
  priorityMaps,
  setPriorityMaps,
  snPriorityOptions,
  snCompanySysId,
  setSnCompanySysId,
  keywords,
  setKeywords,
  searchFields,
  setSearchFields,
  fields,
  handleFieldChange,
  setServices,
  setVendors,
  buildVendorStat,
  setPriorityStats,
  setBugs,
  isConfigured,
  isCve,
  setIsCve,
  syncOnly,
  setSyncOnly,
  loading,
  isServiceActive,
  setIsServiceActive,
  allRequired,
  setAllRequired,
  isUpdated,
  setIsUpdated,
  bugRisk,
  setBugRisk
}) => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const [riskError, setRiskError] = useState('');

  const [activeTab, setActiveTab] = useState('1');
  const [modalOpen, setModalOpen] = useState(false);
  const [validated, setValidated] = useState(null);
  const [isForceRun, setIsForceRun] = useState(false);

  // Use scheduling hook for state and save function
  const {
    timeUnit,
    setTimeUnit,
    timeValue,
    setTimeValue,
    schedulingMode,
    setSchedulingMode,
    schedules,
    setSchedules,
    nextRun,
    setNextRun,
    saveBugServiceFrequency
  } = useScheduling();

  async function setBugServiceFrequency() {
    await saveBugServiceFrequency(vendorId);
  }

  async function saveConfig() {
    // save client config
    const vendorPriorities = priorityMaps.map(mapping => {
      return {
        value: mapping.vendorPriority.value,
        label: mapping.vendorPriority.value,
        snPriority: mapping.snPriority.value
      };
    });

    const data = {
      bugsDateBack: bugsDateBack,
      snAffectedCIQuery: fields.snAffectedCIQuery,
      filterCVEs: isCve,
      vendorPriorities,
      vendorStatuses,
      vendorResolutions,
      excludedKws: keywords,
      searchFields: searchFields,
      snCompanySysId: snCompanySysId.value ? snCompanySysId.value : fields.snCompanySysId,
      type: SETTINGS.VENDOR,
      bugRiskScore: bugRisk
    };

    return BZAPIService.saveSetting(vendorId, syncOnly, data, isForceRun);
  }

  async function handleSave() {
    setRiskError('');
    try {
      setIsSubmitted(true);
      setIsLoading(true);
      // Validate
      if (!bugsDateBack.length || !snCompanySysId) {
        setValidated(false);
        setActiveTab('3');
        setIsLoading(false);
        return;
      }
      if (!priorityMaps.length || !vendorStatuses.length) {
        setValidated(false);
        setActiveTab('4');
        setIsLoading(false);
        return;
      }
      // Validate Risk Score
      const activeRisk = bugRisk.filter(y => {
        return y.active;
      });
      const activeSum = sumBy(activeRisk, risk => {
        return risk.value;
      });

      if (activeSum < 100 && activeSum > 1) {
        console.log('Bug risk share is less than 100');
        setValidated(false);
        setIsLoading(false);
        setActiveTab('5');
        setRiskError('Bug risk share total must be 100');
        return;
      }

      await saveConfig();
      // set updated secretId
      setBugServiceFrequency();
      setIsLoading(false);
      setIsSubmitted(false);
      setModalOpen(false);
      setIsForceRun(false);
      setActiveTab('1');
      toast.success(`Settings Saved!`);
    } catch (e) {
      setIsLoading(false);
      toast.error('Failed to save.');
      console.log('e', e);
    }
  }

  function handleValidationError() {
    setModalOpen(!modalOpen);
    animateScroll.scrollTo(70);
  }

  useEffect(() => {
    setIsUpdated(true);
    if (
      bugsDateBack?.length &&
      snCompanySysId?.value &&
      priorityMaps?.length &&
      !priorityMaps.some(mapping => !mapping.snPriority || !mapping.vendorPriority) &&
      vendorStatuses.length
    ) {
      setAllRequired(true);
    } else {
      setAllRequired(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    fields,
    bugsDateBack,
    vendorStatuses,
    priorityMaps,
    keywords,
    searchFields,
    snCompanySysId,
    isCve,
    vendorResolutions,
    bugRisk
  ]);

  return (
    <>
      <TabNav
        aiSupported={vendor?.aiSupported}
        vendorId={vendorId}
        credentials={false}
        activeTab={activeTab}
        snIntegrationCheck={snIntegrationCheck}
        setActiveTab={setActiveTab}
        setIsLoading={setIsLoading}
        isLoading={isLoading}
        setServices={setServices}
        setVendors={setVendors}
        buildVendorStat={buildVendorStat}
        setPriorityStats={setPriorityStats}
        setBugs={setBugs}
        isConfigured={isConfigured}
        hasBugRisk={true}
        loading={loading}
      />

      <TabContent>
        <TabOne
          vendorId={vendorId}
          vendorLabel={vendorLabel}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          snIntegrationCheck={snIntegrationCheck}
          services={services}
          vendors={vendors}
          priorityStats={priorityStats}
          isConfigured={isConfigured}
          modalOpen={modalOpen}
          setModalOpen={setModalOpen}
          setIsForceRun={setIsForceRun}
          timeUnit={timeUnit}
          setTimeUnit={setTimeUnit}
          timeValue={timeValue}
          setTimeValue={setTimeValue}
          schedulingMode={schedulingMode}
          setSchedulingMode={setSchedulingMode}
          schedules={schedules}
          setSchedules={setSchedules}
          nextRun={nextRun}
          setNextRun={setNextRun}
          loading={loading}
          isServiceActive={isServiceActive}
          setIsServiceActive={setIsServiceActive}
        />
        <DefaultConfigTab
          vendor={vendor}
          activeTab={activeTab}
          vendorLabel={vendorLabel}
          bugsDateBack={bugsDateBack}
          setBugsDateBack={setBugsDateBack}
          isSubmitted={isSubmitted}
          snCompanySysIdErrors={snCompanySysIdErrors}
          snCompanySysIdOptions={snCompanySysIdOptions}
          setSnCompanySysId={setSnCompanySysId}
          snCompanySysId={snCompanySysId}
          fields={fields}
          handleFieldChange={handleFieldChange}
        />
        <form noValidate style={{ minWidth: '100%' }}>
          <TabPane tabId="4">
            {activeTab === '4' && (
              <ProductPriority
                title="Default Bug Configuration"
                vendorPriorityOptions={vendorPriorityOptions}
                vendorStatusOptions={vendorStatusOptions}
                vendorResolutionOptions={vendorResolutionOptions}
                setVendorStatuses={setVendorStatuses}
                vendorStatuses={vendorStatuses}
                isSubmitted={isSubmitted}
                priorityMaps={priorityMaps}
                setVendorResolutions={setVendorResolutions}
                vendorResolutions={vendorResolutions}
                setPriorityMaps={setPriorityMaps}
                snPriorityOptions={snPriorityOptions}
                keywords={keywords}
                setKeywords={setKeywords}
                searchFields={searchFields}
                setSearchFields={setSearchFields}
                isCve={isCve}
                setIsCve={setIsCve}
              />
            )}
          </TabPane>
          <TabPane tabId="8">{activeTab === '8' && <AITab vendorId={vendorId} isReadOnly={isReadOnly} />}</TabPane>
          <div style={{ minWidth: '100%' }}>
            <TabPane tabId="5">
              {activeTab === '5' && <RiskScore bugRisk={bugRisk} setBugRisk={setBugRisk} riskError={riskError} />}
            </TabPane>
          </div>
          <TabPane tabId="6">{activeTab === '6' && <ExecutionStatsTab vendorId={vendorId} />}</TabPane>

          <Row className={`${activeTab === '1' ? 'd-none' : ''} flex-between-center`}>
            <Col xs="auto">
              <Button
                block
                color="falcon-danger"
                onClick={() => {
                  console.log(`/vendors/${vendorId}/configuration`);
                  navigate(-1);
                }}
              >
                Cancel
              </Button>
            </Col>
            <Col xs="auto">
              <SummaryModal
                modalOpen={modalOpen}
                isReadOnly={isReadOnly}
                setModalOpen={setModalOpen}
                handleSave={handleSave}
                isLoading={isLoading}
                vendorLabel={vendorLabel?.replace('Systems', 'Networks')}
                vendorId={vendorId}
                validated={validated}
                setValidated={setValidated}
                syncOnly={syncOnly}
                setSyncOnly={setSyncOnly}
                handleValidationError={handleValidationError}
                allRequired={allRequired}
                isUpdated={isUpdated}
              />
            </Col>
          </Row>
        </form>
      </TabContent>
    </>
  );
};
ConfigFormMongoDB.propTypes = {
  vendor: PropTypes.object,
  isReadOnly: PropTypes.bool,
  settings: PropTypes.object,
  vendorLabel: PropTypes.string,
  vendorId: PropTypes.string,
  vendorPriorityOptions: PropTypes.array,
  vendorStatusOptions: PropTypes.array,
  vendorResolutionOptions: PropTypes.array,
  snCompanySysIdOptions: PropTypes.object,
  snCompanySysId: PropTypes.object,
  snIntegrationCheck: PropTypes.bool,
  services: PropTypes.array,
  vendors: PropTypes.array,
  bugs: PropTypes.object,
  priorityStats: PropTypes.object,
  setPriorityStats: PropTypes.func,
  bugOptions: PropTypes.array,
  selectedBugOption: PropTypes.object,
  setSelectedBugOption: PropTypes.func,
  snCompanySysIdErrors: PropTypes.bool,
  vendorEventScopes: PropTypes.array,
  setVendorEventScopes: PropTypes.func,
  vendorResolutions: PropTypes.array,
  setVendorResolutions: PropTypes.func,
  setVendorStatuses: PropTypes.func,
  vendorStatuses: PropTypes.array,
  fields: PropTypes.object,
  handleFieldChange: PropTypes.func,
  priorityMaps: PropTypes.array,
  setPriorityMaps: PropTypes.func,
  snPriorityOptions: PropTypes.array,
  keywords: PropTypes.array,
  setKeywords: PropTypes.func,
  searchFields: PropTypes.array,
  setSearchFields: PropTypes.func,
  setSnCompanySysId: PropTypes.func,
  bugsDateBack: PropTypes.string,
  setBugsDateBack: PropTypes.func,
  setServices: PropTypes.func,
  setVendors: PropTypes.func,
  buildVendorStat: PropTypes.func,
  setBugs: PropTypes.func,
  isConfigured: PropTypes.bool,
  isCve: PropTypes.bool,
  setIsCve: PropTypes.func,
  syncOnly: PropTypes.bool,
  setSyncOnly: PropTypes.func,
  loading: PropTypes.bool,
  setIsServiceActive: PropTypes.func,
  isServiceActive: PropTypes.number,
  allRequired: PropTypes.bool,
  isUpdated: PropTypes.bool,
  setAllRequired: PropTypes.func,
  bugRisk: PropTypes.array,
  setBugRisk: PropTypes.func,
  setIsUpdated: PropTypes.func
};
export default ConfigFormMongoDB;
